/**
 * Entradas Table Component - Lista de entradas finalizadas
 */

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PermissionGate } from "@/components/PermissionGate";
import { Edit, Trash2, Eye, FileText, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { EntradaCompleta } from "@/types/entrada";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/utils";

type SortField = 
  | 'data_entrada' 
  | 'produto_nome' 
  | 'quantidade_compra' 
  | 'total_geral_documento' 
  | 'fornecedor_nome';

interface EntradasTableProps {
  entradas: EntradaCompleta[];
  onView: (entrada: EntradaCompleta) => void;
  onEdit: (entrada: EntradaCompleta) => void;
  onDelete: (entrada: EntradaCompleta) => void;
  sortField?: SortField | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: (field: SortField) => void;
}


export function EntradasTable({
  entradas,
  onView,
  onEdit,
  onDelete,
  sortField,
  sortDirection,
  onSort,
}: EntradasTableProps) {
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="ml-2 h-4 w-4" />;
    }
    return <ArrowUpDown className="ml-2 h-4 w-4" />;
  };

  if (entradas.length === 0) {
    return (
      <div className="border rounded-lg py-12 text-center">
        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Nenhuma entrada encontrada</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort?.('data_entrada')}
                className="hover:bg-accent"
              >
                Data
                {getSortIcon('data_entrada')}
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort?.('produto_nome')}
                className="hover:bg-accent"
              >
                Produto
                {getSortIcon('produto_nome')}
              </Button>
            </TableHead>
            <TableHead className="text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort?.('quantidade_compra')}
                className="hover:bg-accent"
              >
                Qtd
                {getSortIcon('quantidade_compra')}
              </Button>
            </TableHead>
            <TableHead>Unid. Padrão</TableHead>
            <TableHead className="text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort?.('total_geral_documento')}
                className="hover:bg-accent"
              >
                Total
                {getSortIcon('total_geral_documento')}
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort?.('fornecedor_nome')}
                className="hover:bg-accent"
              >
                Fornecedor
                {getSortIcon('fornecedor_nome')}
              </Button>
            </TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entradas.map((entrada) => {
            const item = entrada.itens[0];
            return (
              <TableRow key={entrada.id}>
                <TableCell className="font-medium">
                  {format(new Date(entrada.cabecalho.data_entrada), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {item?.produto_nome || "-"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-sm font-medium">
                    {item?.quantidade_compra 
                      ? `${item.quantidade_compra}` 
                      : "-"}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {item?.unidade_padrao || "-"}
                  </span>
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(entrada.totais.total_geral_documento)}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {entrada.cabecalho.fornecedor_nome || "-"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <PermissionGate resource="stock_entries" action="view">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 md:h-9 md:w-9 touch-manipulation"
                        onClick={() => onView(entrada)}
                        title="Visualizar entrada"
                      >
                        <Eye className="w-5 h-5 md:w-4 md:h-4" />
                      </Button>
                    </PermissionGate>
                    <PermissionGate resource="stock_entries" action="edit">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 md:h-9 md:w-9 touch-manipulation"
                        onClick={() => onEdit(entrada)}
                        title="Editar entrada"
                      >
                        <Edit className="w-5 h-5 md:w-4 md:h-4" />
                      </Button>
                    </PermissionGate>
                    <PermissionGate resource="stock_entries" action="delete">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 md:h-9 md:w-9 touch-manipulation text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => onDelete(entrada)}
                        title="Excluir entrada"
                      >
                        <Trash2 className="w-5 h-5 md:w-4 md:h-4" />
                      </Button>
                    </PermissionGate>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
